import type {EditTaskFormPayload} from "./edit-task.schema";
import type {MutationCallbacks} from "../../../../types/callbacks";
import type {Task} from "@project/sdk/admin";

import {useMutation, useQueryClient} from "@tanstack/react-query";

import {userKeys} from "../../../../user-keys";

import {editTaskMutationOptions} from "./edit-task.mutation-options";

/**
 * Command hook: Edit an existing task on a user.
 *
 * DEFAULT BEHAVIOR (always applied):
 * - Invalidate the user detail query so the updated task data is refreshed
 *
 * USER CALLBACKS (optional, applied after defaults):
 * - onSuccess: e.g., show toast, close modal
 * - onError: e.g., show error toast
 *
 * @param userId - The ID of the user who owns the task
 * @param taskId - The ID of the task to edit
 * @param callbacks - Optional callbacks for custom logic
 *
 * @example
 * const mutation = useEditTask(userId, taskId, {
 *   onSuccess: (task) => showToast(`Task "${task.title}" updated`),
 *   onError: (err) => showToast(`Error: ${err.message}`),
 * });
 */
export function useEditTask(
  userId: string,
  taskId: string,
  callbacks?: Partial<MutationCallbacks<EditTaskFormPayload, Task>>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...editTaskMutationOptions(userId, taskId),

    onSuccess: (task) => {
      // ─── DEFAULT: Invalidate user detail so updated task appears ──
      void queryClient.invalidateQueries({queryKey: userKeys.detail(userId)});

      // ─── USER: Custom onSuccess callback ──────────────────────────
      callbacks?.onSuccess?.(task);
    },

    onError: (err, vars, ctx) => {
      // ─── USER: Custom onError callback ───────────────────────────
      callbacks?.onError?.(err, vars, ctx);
    },
  });
}
